import { useEffect, useRef, useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";
import RichTextEditor, { RichTextDisplay } from "./RichTextEditor";
import {
  getOrCreateConversation,
  sendMessage,
  subscribeToMessages,
  subscribeToConversation,
  subscribeToAllConversations,
  markConversationRead,
  toggleMessageReaction,
} from "./directMessages";


const REACTION_EMOJIS = ["❤️", "🔥", "🙌", "😂", "👍", "🎉"];

function formatTime(ts) {
  if (!ts) return "";
  const d = ts.toDate?.() || new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

const card = {
  background: "#f8fbff",
  borderRadius: "18px",
  border: "2px solid #bfdbfe",
};

// ── Coach inbox: list of all conversations ──────────────────────────────────

function CoachInbox({ currentUser, allUsers, onOpen }) {
  const [conversations, setConversations] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const unsub = subscribeToAllConversations(setConversations);
    return unsub;
  }, []);

  const existingIds = new Set(conversations.map((c) => c.id));
  const filteredUsers = (allUsers || []).filter((u) =>
    !existingIds.has(u.id) &&
    (u.name || "").toLowerCase().includes(search.toLowerCase())
  );

  async function startNewConversation(user) {
    const conv = await getOrCreateConversation(user.id, user.name, user.avatar);
    setShowPicker(false);
    setSearch("");
    onOpen(conv);
  }

  return (
    <div style={{ display: "grid", gap: "10px" }}>
      {/* New message button */}
      <button
        onClick={() => setShowPicker((p) => !p)}
        style={{ padding: "10px 16px", borderRadius: "14px", border: "2px solid #2563eb", background: showPicker ? "#e0f2fe" : "#f8fbff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer", textAlign: "left", fontSize: "14px" }}
      >
        ✏️ New message
      </button>

      {/* Client picker */}
      {showPicker && (
        <div style={{ ...card, padding: "14px", display: "grid", gap: "10px" }}>
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients..."
            style={{ padding: "10px 14px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "14px", outline: "none" }}
          />
          {filteredUsers.length === 0 && (
            <div style={{ color: "#6b8cb0", fontSize: "13px" }}>
              {(allUsers || []).length === 0 ? "No clients found." : "All clients already have conversations."}
            </div>
          )}
          {filteredUsers.map((u) => (
            <button
              key={u.id}
              onClick={() => startNewConversation(u)}
              style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f8fbff", cursor: "pointer", textAlign: "left" }}
            >
              {u.avatar ? (
                <img src={u.avatar} alt={u.name} style={{ width: "36px", height: "36px", borderRadius: "999px", objectFit: "cover", border: "1px solid #bfdbfe", flexShrink: 0 }} />
              ) : (
                <div style={{ width: "36px", height: "36px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
              )}
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>{u.name || "Client"}</div>
            </button>
          ))}
        </div>
      )}

      {conversations.length === 0 && !showPicker && (
        <div style={{ color: "#4b6b8a", padding: "16px", textAlign: "center" }}>
          No conversations yet — click "New message" to start one.
        </div>
      )}
      {conversations.map((conv) => (
        <button
          key={conv.id}
          onClick={() => onOpen(conv)}
          style={{
            ...card,
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "14px",
            cursor: "pointer",
            border: conv.coachUnread > 0 ? "2px solid #2563eb" : "2px solid #bfdbfe",
            background: conv.coachUnread > 0 ? "#e0f2fe" : "#f8fbff",
            textAlign: "left",
          }}
        >
          {conv.clientAvatar ? (
            <img src={conv.clientAvatar} alt={conv.clientName}
              style={{ width: "44px", height: "44px", borderRadius: "999px", objectFit: "cover", border: "2px solid #bfdbfe", flexShrink: 0 }} />
          ) : (
            <div style={{ width: "44px", height: "44px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: "6px", minWidth: 0 }}>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {conv.clientName || "Client"}
                </div>
                {(() => {
                  const u = (allUsers || []).find((u) => u.id === conv.id);
                  return u?.programTag ? (
                    <span style={{ fontSize: "10px", color: "#6b8cb0", background: "#dbeafe", borderRadius: "6px", padding: "1px 5px", whiteSpace: "nowrap", flexShrink: 0, fontWeight: "600" }}>
                      {u.programTag}
                    </span>
                  ) : null;
                })()}
              </div>
              <div style={{ color: "#6b8cb0", fontSize: "11px", flexShrink: 0 }}>{formatTime(conv.lastMessageAt)}</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", marginTop: "2px" }}>
              <div style={{ color: "#4b6b8a", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {conv.lastMessage ? conv.lastMessage.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() || "No messages yet" : "No messages yet"}
              </div>
              {conv.coachUnread > 0 && (
                <div style={{ background: "#2563eb", color: "#fff", borderRadius: "999px", minWidth: "20px", height: "20px", fontSize: "11px", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px", flexShrink: 0 }}>
                  {conv.coachUnread}
                </div>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ── Attachment rendering ────────────────────────────────────────────────────

function AttachmentDisplay({ attachment, isMine }) {
  const [imgOpen, setImgOpen] = useState(false);
  if (attachment.type === "image") {
    return (
      <>
        <img
          src={attachment.url}
          alt="attachment"
          onClick={() => setImgOpen(true)}
          style={{ maxWidth: "220px", maxHeight: "240px", borderRadius: "12px", objectFit: "cover", cursor: "zoom-in", display: "block", marginTop: "4px" }}
        />
        {imgOpen && (
          <div
            onClick={() => setImgOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
          >
            <img src={attachment.url} alt="full" style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: "12px", objectFit: "contain" }} />
          </div>
        )}
      </>
    );
  }
  if (attachment.type === "link") {
    const display = attachment.label && attachment.label !== attachment.url
      ? attachment.label
      : (() => { try { return new URL(attachment.url).hostname; } catch { return attachment.url; } })();
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "flex", alignItems: "center", gap: "10px", marginTop: "6px",
          padding: "10px 12px", borderRadius: "12px", textDecoration: "none",
          background: isMine ? "rgba(255,255,255,0.15)" : "#eff6ff",
          border: isMine ? "1px solid rgba(255,255,255,0.25)" : "1.5px solid #bfdbfe",
        }}
      >
        <span style={{ fontSize: "18px", flexShrink: 0 }}>🔗</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: "bold", fontSize: "13px", color: isMine ? "#fff" : "#1e40af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {display}
          </div>
          {attachment.label && attachment.label !== attachment.url && (
            <div style={{ fontSize: "11px", color: isMine ? "rgba(255,255,255,0.7)" : "#6b8cb0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {(() => { try { return new URL(attachment.url).hostname; } catch { return attachment.url; } })()}
            </div>
          )}
        </div>
        <span style={{ fontSize: "12px", color: isMine ? "rgba(255,255,255,0.6)" : "#93c5fd", flexShrink: 0 }}>↗</span>
      </a>
    );
  }
  return null;
}

// ── Shared chat thread ──────────────────────────────────────────────────────

function ChatThread({ conversationId, currentUser, isCoach, otherName, otherAvatar, onBack }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pickerOpenId, setPickerOpenId] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef();
  const fileRef = useRef();

  useEffect(() => {
    if (!conversationId) return;
    markConversationRead(conversationId, isCoach);
    const unsub = subscribeToMessages(conversationId, setMessages);
    return unsub;
  }, [conversationId, isCoach]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSend() {
    const text = draft.trim();
    if ((!text && attachments.length === 0) || sending) return;
    setSending(true);
    const toSend = [...attachments];
    setDraft("");
    setAttachments([]);
    try {
      await sendMessage({
        conversationId,
        senderId: currentUser.uid,
        senderName: currentUser.name,
        senderAvatar: currentUser.avatar,
        text,
        attachments: toSend,
        isFromCoach: isCoach,
      });
    } finally {
      setSending(false);
    }
  }

  async function handleImageSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const path = `dm-attachments/${conversationId}/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setAttachments((prev) => [...prev, { type: "image", url }]);
    } catch (err) {
      console.error("DM image upload failed:", err);
    } finally {
      setUploading(false);
    }
  }

  function handleAddLink() {
    const raw = linkUrl.trim();
    if (!raw) return;
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    setAttachments((prev) => [...prev, { type: "link", url, label: linkLabel.trim() || url }]);
    setLinkUrl("");
    setLinkLabel("");
    setShowLinkForm(false);
  }

  const canSend = (draft.trim() || attachments.length > 0) && !sending;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 220px)" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
        {onBack && (
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#2563eb", fontWeight: "bold", cursor: "pointer", fontSize: "20px", padding: 0, lineHeight: 1 }}>←</button>
        )}
        {otherAvatar && !avatarError ? (
          <img src={otherAvatar} alt={otherName}
            onError={() => setAvatarError(true)}
            style={{ width: "38px", height: "38px", borderRadius: "999px", objectFit: "cover", border: "2px solid #bfdbfe" }} />
        ) : (
          <div style={{ width: "38px", height: "38px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
        )}
        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>{otherName}</div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px", paddingBottom: "8px" }}>
        {messages.length === 0 && (
          <div style={{ color: "#6b8cb0", fontSize: "13px", textAlign: "center", marginTop: "40px" }}>
            No messages yet — say hi!
          </div>
        )}
        {messages.map((msg) => {
          const isMine = msg.senderId === currentUser.uid;
          const msgReactions = msg.reactions || {};
          const reactionEntries = REACTION_EMOJIS
            .map((e) => ({ emoji: e, uids: msgReactions[e] || [] }))
            .filter((r) => r.uids.length > 0);
          const pickerOpen = pickerOpenId === msg.id;
          const msgAttachments = msg.attachments || [];

          return (
            <div key={msg.id} style={{ display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start", gap: "2px" }}>
              <div style={{ display: "flex", flexDirection: isMine ? "row-reverse" : "row", alignItems: "flex-end", gap: "8px" }}>
                {!isMine && (
                  msg.senderAvatar ? (
                    <img src={msg.senderAvatar} alt={msg.senderName}
                      style={{ width: "28px", height: "28px", borderRadius: "999px", objectFit: "cover", border: "1px solid #bfdbfe", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: "28px", height: "28px", borderRadius: "999px", background: "#bfdbfe", flexShrink: 0 }} />
                  )
                )}
                <div style={{ display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start", gap: "4px", maxWidth: "72%" }}>
                  <div style={{
                    padding: "10px 14px",
                    borderRadius: isMine ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    background: isMine ? "#2563eb" : "#f8fbff",
                    color: isMine ? "#fff" : "#243b53",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    border: isMine ? "none" : "2px solid #bfdbfe",
                    wordBreak: "break-word",
                    textAlign: "left",
                  }}>
                    {msg.text && <RichTextDisplay html={msg.text} style={{ color: "inherit" }} />}
                    {msgAttachments.map((att, i) => (
                      <AttachmentDisplay key={i} attachment={att} isMine={isMine} />
                    ))}
                  </div>

                  {/* Reaction chips */}
                  {reactionEntries.length > 0 && (
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {reactionEntries.map(({ emoji, uids }) => {
                        const iReacted = uids.includes(currentUser.uid);
                        return (
                          <button
                            key={emoji}
                            onClick={() => toggleMessageReaction({ conversationId, messageId: msg.id, emoji, uid: currentUser.uid })}
                            style={{
                              padding: "2px 8px", borderRadius: "999px",
                              border: iReacted ? "2px solid #2563eb" : "2px solid #bfdbfe",
                              background: iReacted ? "#e0f2fe" : "#f8fbff",
                              cursor: "pointer", fontSize: "13px",
                              display: "flex", alignItems: "center", gap: "3px", color: "#1e3a5f",
                            }}
                          >
                            {emoji} <span style={{ fontSize: "11px", fontWeight: "bold" }}>{uids.length}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Emoji picker toggle */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "flex-end", gap: "2px" }}>
                  <button
                    onClick={() => setPickerOpenId(pickerOpen ? null : msg.id)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#93c5fd", padding: "2px", lineHeight: 1 }}
                  >
                    {pickerOpen ? "✕" : "☺"}
                  </button>
                  <div style={{ fontSize: "10px", color: "#93c5fd" }}>{formatTime(msg.createdAt)}</div>
                </div>
              </div>

              {/* Emoji picker row */}
              {pickerOpen && (
                <div style={{
                  display: "flex", gap: "6px", background: "#f8fbff",
                  border: "2px solid #bfdbfe", borderRadius: "999px", padding: "6px 12px",
                  marginLeft: isMine ? 0 : "36px", marginRight: isMine ? "36px" : 0,
                }}>
                  {REACTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => { toggleMessageReaction({ conversationId, messageId: msg.id, emoji, uid: currentUser.uid }); setPickerOpenId(null); }}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", padding: "2px", lineHeight: 1 }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Compose area */}
      <div style={{ marginTop: "10px" }}>
        {/* Link form */}
        {showLinkForm && (
          <div style={{ marginBottom: "8px", padding: "12px", borderRadius: "14px", background: "#f0f9ff", border: "1.5px solid #bfdbfe" }}>
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "8px" }}>🔗 Add Link</div>
            <input
              autoFocus
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddLink(); } }}
              placeholder="https://..."
              style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "14px", outline: "none", boxSizing: "border-box", marginBottom: "6px" }}
            />
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddLink(); } }}
              placeholder="Label (optional)"
              style={{ width: "100%", padding: "9px 12px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "14px", outline: "none", boxSizing: "border-box", marginBottom: "8px" }}
            />
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                onClick={handleAddLink}
                disabled={!linkUrl.trim()}
                style={{ flex: 1, padding: "8px", borderRadius: "10px", border: "none", background: linkUrl.trim() ? "#2563eb" : "#bfdbfe", color: "#fff", fontWeight: "bold", fontSize: "13px", cursor: linkUrl.trim() ? "pointer" : "default" }}
              >Add</button>
              <button
                onClick={() => { setShowLinkForm(false); setLinkUrl(""); setLinkLabel(""); }}
                style={{ padding: "8px 14px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#4b6b8a", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
              >Cancel</button>
            </div>
          </div>
        )}

        {/* Pending attachments */}
        {attachments.length > 0 && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
            {attachments.map((att, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 10px", borderRadius: "999px", background: "#e0f2fe", border: "1.5px solid #bfdbfe" }}>
                <span style={{ fontSize: "13px" }}>{att.type === "image" ? "📷" : "🔗"}</span>
                <span style={{ fontSize: "12px", color: "#1e3a5f", fontWeight: "bold", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {att.type === "image" ? "Image" : (att.label || att.url)}
                </span>
                <button
                  onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", color: "#6b8cb0", cursor: "pointer", fontSize: "14px", padding: 0, lineHeight: 1, flexShrink: 0 }}
                >×</button>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar + input */}
        <div style={{ display: "flex", gap: "6px", alignItems: "flex-end" }}>
          {/* Attachment buttons */}
          <div style={{ display: "flex", gap: "4px", flexShrink: 0, paddingBottom: "2px" }}>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              title="Send image"
              style={{ width: "36px", height: "36px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", cursor: uploading ? "default" : "pointer", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: "#4b6b8a" }}
            >
              {uploading ? "⏳" : "📷"}
            </button>
            <button
              onClick={() => setShowLinkForm((p) => !p)}
              title="Share a link"
              style={{ width: "36px", height: "36px", borderRadius: "10px", border: showLinkForm ? "1.5px solid #2563eb" : "1.5px solid #bfdbfe", background: showLinkForm ? "#e0f2fe" : "#f5faff", cursor: "pointer", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: showLinkForm ? "#2563eb" : "#4b6b8a" }}
            >
              🔗
            </button>
          </div>

          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageSelect} />

          <div style={{ flex: 1 }}>
            <RichTextEditor
              value={draft}
              onChange={setDraft}
              placeholder="Type a message..."
              rows={1}
              onSubmit={handleSend}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!canSend}
            style={{
              padding: "12px 18px", borderRadius: "16px", border: "none",
              background: canSend ? "#2563eb" : "#bfdbfe",
              color: "#fff", fontWeight: "bold",
              cursor: canSend ? "pointer" : "default",
              fontSize: "15px", flexShrink: 0,
            }}
          >
            ↑
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main export ─────────────────────────────────────────────────────────────

export default function DirectMessages({ currentUser, isCoach, coachInfo: coachInfoProp, allUsers }) {
  const [view, setView] = useState("thread"); // coach sees "inbox" | "thread", client always sees "thread"
  const [activeConv, setActiveConv] = useState(null);
  const [convReady, setConvReady] = useState(false);
  const [resolvedCoachInfo, setResolvedCoachInfo] = useState(coachInfoProp || null);

  useEffect(() => {
  if (isCoach) return;
  setResolvedCoachInfo(coachInfoProp || null);
}, [isCoach, coachInfoProp]);

  // Clients always have exactly one conversation (with the coach)
  useEffect(() => {
    if (isCoach) {
      setView("inbox");
      setConvReady(true);
      return;
    }
    // Client: ensure their conversation doc exists
    getOrCreateConversation(currentUser.uid, currentUser.name, currentUser.avatar).then((conv) => {
      setActiveConv(conv);
      setConvReady(true);
    }).catch((err) => console.error("DirectMessages: could not load coach info:", err?.message));
  }, [currentUser.uid, isCoach]);

  if (!convReady) return <div style={{ color: "#4b6b8a", padding: "20px" }}>Loading...</div>;

  // ── Coach ─────────────────────────────────────────────────────────────────
  if (isCoach) {
    if (view === "thread" && activeConv) {
      return (
        <ChatThread
          conversationId={activeConv.id}
          currentUser={currentUser}
          isCoach
          otherName={activeConv.clientName || "Client"}
          otherAvatar={activeConv.clientAvatar}
          onBack={() => { setView("inbox"); setActiveConv(null); }}
        />
      );
    }
    return (
      <CoachInbox
        currentUser={currentUser}
        allUsers={allUsers}
        onOpen={(conv) => { setActiveConv(conv); setView("thread"); markConversationRead(conv.id, true); }}
      />
    );
  }

  // ── Client ────────────────────────────────────────────────────────────────
  return (
    <ChatThread
      conversationId={currentUser.uid}
      currentUser={currentUser}
      isCoach={false}
      otherName={resolvedCoachInfo?.name || "Your Coach"}
      otherAvatar={resolvedCoachInfo?.avatar || null}
      onBack={null}
    />
  );
}
